import React, { useContext } from 'react';
import { IEquipmentTypeOrAll, IFormattedStat } from '@ifit/gateway-wolf-dashboard-service';
import { ThemeContext } from '@ifit/themes/ThemeContext';
import { shark, white } from '@ifit/base-coat/js/colors';
import { formatNum } from './helpers';
import cn from 'classnames';
import DotsLoading from '@ifit/mp-dots-loading';
import Icon from '@ifit/react-icons/dist/components/icon';
import CHEVRON_DOWN_LINE from '@ifit/react-icons/dist/icons/CHEVRON_DOWN_LINE';
import LOCKED_SOLID from '@ifit/react-icons/dist/icons/LOCKED_SOLID';
import { Text } from './text';
import Carousel from 'react-multi-carousel';
import { Listbox } from '@headlessui/react';
import 'react-multi-carousel/lib/styles.css';
import './user-stats.scss';

const responsive = {
  desktop: {
    breakpoint: {
      max: 4000,
      min: 1025,
    },
    items: 3,
    slidesToSlide: 3,
  },
  tablet: {
    breakpoint: {
      max: 1024,
      min: 768,
    },
    items: 3,
    slidesToSlide: 3,
  },
  mobile: {
    breakpoint: {
      max: 767,
      min: 0,
    },
    items: 3,
    slidesToSlide: 3,
  },
};

const themeColors = {
  LIGHT: {
    color: shark,
  },
  DARK: {
    color: white,
  },
  VAULT: {
    color: shark,
  },
};

export interface EquipmentTypeOption {
  value: IEquipmentTypeOrAll;
  label: string;
}

interface ITimeRange {
  key: string;
  name: string;
}

interface IProps {
  equipmentTypeOptions: EquipmentTypeOption[];
  stats: IFormattedStat[];
  selectedEquipmentType: IEquipmentTypeOrAll;
  selectedTimeRange: ITimeRange;
  timeRanges: ITimeRange[];
  updateEquipmentType: React.Dispatch<React.SetStateAction<IEquipmentTypeOrAll>>;
  updateTimeRange: React.Dispatch<React.SetStateAction<ITimeRange>>;
  loading?: boolean;
  locked?: boolean;
}

export default function UserStats({
  equipmentTypeOptions,
  loading,
  locked,
  stats,
  selectedEquipmentType,
  selectedTimeRange,
  timeRanges,
  updateEquipmentType,
  updateTimeRange,
}: IProps): JSX.Element {
  const { theme } = useContext(ThemeContext);

  const isLongText = stats.some(
    (stat) =>
      (stat.type === 'cals' && stat.unit.length > 4) ||
      (stat.type === 'avg-watts' && stat.unit.length > 5) ||
      (stat.type === 'pace' && stat.unit.length > 3)
  );

  const selectedEquipmentTypeOption = equipmentTypeOptions.find((opt) => opt.value === selectedEquipmentType);

  return (
    <div className="mp-user-stats">
      <div className="mp-user-stats-header">
        {locked ? (
          <>
            <p className="mp-user-stats-selector">{Text.title}</p>
            <Icon icon={LOCKED_SOLID} size={24} color={themeColors[theme].color} />
          </>
        ) : (
          <>
            <div className="mp-user-stats-list-container">
              <Listbox value={selectedTimeRange} onChange={updateTimeRange}>
                <Listbox.Button className="mp-user-stats-selector">
                  {selectedTimeRange.name} <Icon icon={CHEVRON_DOWN_LINE} size={24} color={themeColors[theme].color} />
                </Listbox.Button>

                <Listbox.Options className="mp-user-stats-options">
                  {timeRanges.map((time) => (
                    <Listbox.Option key={time.key} value={time} className="mp-user-stats-option">
                      {time.name}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>

              {loading && <DotsLoading color={themeColors[theme].color} size={4} />}
            </div>
            <div className="mp-user-stats-list-container">
              <Listbox value={selectedEquipmentTypeOption.value} onChange={updateEquipmentType}>
                <Listbox.Button className="mp-user-stats-selector">
                  {selectedEquipmentTypeOption.label}
                  <Icon icon={CHEVRON_DOWN_LINE} size={24} color={themeColors[theme].color} />
                </Listbox.Button>

                <Listbox.Options className="mp-user-stats-options mp-user-stats-options-align-end">
                  {equipmentTypeOptions.map((option) => (
                    <Listbox.Option key={option.value} value={option.value} className="mp-user-stats-option">
                      {option.label}
                    </Listbox.Option>
                  ))}
                </Listbox.Options>
              </Listbox>
            </div>
          </>
        )}
      </div>

      <div className="mp-user-stats-list">
        <Carousel responsive={responsive} arrows={false} showDots renderDotsOutside dotListClass="mp-user-stats-dots">
          {stats.map((stat) => (
            <div key={`stat-${stat.type}`} className="mp-user-stats-stat">
              <strong className="mp-user-stats-stat-title">{stat.label}</strong>
              <p
                className={cn('mp-user-stats-stat-info', {
                  'mp-user-stats-stat-small-text': isLongText,
                })}
              >
                {formatNum(stat.formatted)}
                {stat.unit.length > 0 && <span className="mp-user-stats-stat-unit">{stat.unit}</span>}
              </p>
            </div>
          ))}
        </Carousel>
      </div>
    </div>
  );
}
